import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { logActivity } from '../utils/logActivity';
import { retrogradeStudyLabel, retroComplementLabels } from '../utils/planetAbbreviations';
import { useAuth } from '../context/AuthContext';
import { withFrame } from '../utils/framePrefix';
import { isAdminUser } from '../utils/isAdminUser';
import { useInitialConfig } from '../utils/useInitialConfig';

import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Select, MenuItem, Box, Typography, TextField
} from '@mui/material';
import HelpIcon from './HelpIcon';
import { useChartOverlays } from '../context/ChartOverlayContext';

import { addAreaHighlightOverlaysAndMessages } from './addAreaHighlightOverlaysUtil';
import { complementShades } from '../utils/overlayShades';


const PLANETS = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];
// Ketu joins Rahu in the exclusion: both nodes move backwards permanently, so a direct/retrograde
// study of either asks about a station that never happens.
const RETRO_PLANETS = PLANETS.filter(p => p !== 'Sun' && p !== 'Moon' && p !== 'Rahu' && p !== 'Ketu');
const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
const NAKSHATRAS = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
  'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
  'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
  'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta',
  'Shatabhisha', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
];
/**
 * Frames a study can be computed in.
 *
 * Two independent choices, so four modes. Where the observer stands — Earth (geo) or the Sun
 * (helio) — decides which bodies exist and whether retrograde motion is in play. Where the zodiac
 * starts — the equinox (tropical) or the fixed-star reference (sidereal, the Lahiri ayanamsha away
 * from it) — only relabels degrees; it moves no body.
 *
 * The names are the ones the backend resolves: ZodiacFrame.from() on the Java side.
 */
const ZODIAC_TYPES = ['geo-sidereal', 'geo-tropical'];

/**
 * BOTH HELIOCENTRIC FRAMES ARE ADMIN-ONLY, as of 2026-10-19.
 *
 * <h2>The history, because this list has now been emptied once and refilled</h2>
 *
 * `helio-sidereal` alone used to be here, on the grounds that the combination has no established
 * interpretation — heliocentric positions are well defined and sidereal boundaries are well
 * defined, but pairing them is not a traditional reading. That restriction was removed because it
 * was an argument about what a result MEANS, and two of the four cells of a 2x2 being available
 * read as a defect rather than as curation.
 *
 * <p>This is a different decision from that one. It is not about whether a frame is interpretable
 * — it is about which audience the study surface is for, the same question the toolbar's role gate
 * answers for the engine runners. An admin keeps the whole 2x2; a customer gets the geocentric
 * pair, which is the complete grid for the observer position they work in.
 *
 * <p>Whoever emptied this list left the machinery standing and wrote "put a frame back here to
 * restrict it again; nothing else needs to change". That held: the reset effect, `zodiacTypesFor`
 * and their auth-timing handling all read this list and needed no edit.
 *
 * <h2>⚠ THE ORDER IS LOAD-BEARING FOR AN ADMIN</h2>
 *
 * `zodiacTypesFor` concatenates this after {@link ZODIAC_TYPES}, so an admin sees geo-sidereal,
 * geo-tropical, helio, helio-sidereal — the order the menu has always had. Reversing these two
 * would silently reorder an admin's dropdown.
 *
 * <h2>And this is PRESENTATION — the endpoint enforces it independently</h2>
 *
 * A hidden option is not an inaccessible one: the request is four fields of JSON and a saved study
 * can carry any frame string. `AreaHighlightAccessGuard` answers 403 to a non-admin asking for
 * either heliocentric frame, whatever put it on the wire.
 */
const ADMIN_ONLY_ZODIAC_TYPES = ['helio', 'helio-sidereal'];

/**
 * What each frame is called on screen.
 *
 * The wire values stay as they are — they are what the backend's ZodiacFrame.from() resolves and
 * what a saved study records — but "helio" and "geo-sidereal" say nothing to a reader about which
 * of the two choices they are making. The label spells out both: centre first, then where the
 * zodiac starts.
 */
const ZODIAC_LABELS = {
  'geo-sidereal': 'Geocentric Sidereal',
  'geo-tropical': 'Geocentric Tropical',
  'helio': 'Heliocentric Tropical',
  'helio-sidereal': 'Heliocentric Sidereal (Lahiri)',

  // The pre-rename spellings, which ZodiacFrame.from() still accepts on the backend. Nothing in
  // this dialog puts them in the menu — the list is built from ZODIAC_TYPES — but zodiacTypesFor()
  // adds whatever is currently SELECTED to the options so the Select never renders blank, and a
  // selection restored from an older saved study or a stored preference could still be one of
  // these. Mapping them means such a value reads as its frame rather than as a raw token.
  'sidereal': 'Geocentric Sidereal',
  'tropical': 'Geocentric Tropical',
  'heliocentric': 'Heliocentric Tropical',
};

/** The on-screen name for a frame, falling back to the wire value for anything unmapped. */
const zodiacLabel = (frame) => ZODIAC_LABELS[frame] || frame;

/**
 * The frames this user may pick from, always including whatever is currently selected.
 *
 * The second part matters during a token refresh. Auth briefly reports every user as a
 * non-admin, the list contracts to three, and a Select whose value is no longer among its options
 * renders BLANK — the same trap the planet list has to avoid when the frame turns heliocentric.
 * The selection itself is kept (see the reset effect, which waits for auth to settle), so dropping
 * it from the list would show an empty box for a frame that is still very much selected.
 *
 * This is presentation only. The backend answers 403 for a non-admin asking for EITHER
 * heliocentric frame however the option got on screen, and a user who really is not an admin has
 * the selection reset out from under this the moment auth resolves.
 */
const zodiacTypesFor = (isAdmin, selected) => {
  const frames = isAdmin ? [...ZODIAC_TYPES, ...ADMIN_ONLY_ZODIAC_TYPES] : [...ZODIAC_TYPES];
  if (selected && !frames.includes(selected)) frames.push(selected);
  return frames;
};

/*
 * Whether this user may be offered the admin-only frame: `isAdminUser`, now shared with the Message
 * Centre rather than defined here.
 *
 * It is deliberately more than the context's `isAdmin` flag, and the reason belongs next to this
 * call site: AuthContext falls back to `{ uid, email, fallback: true }` whenever the /api/user/me
 * fetch fails with anything other than a 401 — a 404, a 500, a dropped connection. The session then
 * carries no subscriptionType at all, every admin-only control quietly disappears for the rest of
 * it, and nothing on screen says why. That is the most likely reason for a fourth option that
 * "should be there" not being there. See utils/isAdminUser.js for what it reads instead.
 */

/** True for both frames centred on the Sun. */
const isHeliocentricFrame = (frame) => frame === 'helio' || frame === 'helio-sidereal';

/**
 * Bodies with no heliocentric position.
 *
 * The Sun is the origin of that frame. Rahu and Ketu are the lunar nodes — intersections of the
 * Moon's orbit around EARTH with the ecliptic — so they are undefined without Earth at the centre,
 * and the ephemeris fails outright rather than returning a poor answer. Asking for one is what
 * produced an HTTP 500 on the Longitude study.
 *
 * The Moon is deliberately NOT here: its heliocentric longitude tracks Earth's, which makes it the
 * usable proxy for Earth in that frame.
 */
const NO_HELIOCENTRIC_POSITION = ['Sun', 'Rahu', 'Ketu', 'North Node', 'South Node'];

/** Bodies selectable in a given frame. */
const planetsForFrame = (frame) => (isHeliocentricFrame(frame)
  ? PLANETS.filter(p => !NO_HELIOCENTRIC_POSITION.includes(p))
  : PLANETS);

/** Where a selection lands when the chosen body is unavailable in the new frame. */
const fallbackPlanet = (frame) => {
  const valid = planetsForFrame(frame);
  // Mercury rather than the list head, which is the Sun's slot and reads as an arbitrary jump.
  return valid.includes('Mercury') ? 'Mercury' : (valid[0] || '');
};
const CHARANS = [1, 2, 3, 4];


const OVERLAY_COLORS = [
  'rgba(229, 57, 53, 0.55)',
  'rgba(30, 136, 229, 0.55)',
  'rgba(67, 160, 71, 0.55)',
  'rgba(244, 81, 30, 0.55)',
  'rgba(142, 36, 170, 0.55)',
  'rgba(255, 195, 0, 0.55)',
  'rgba(255, 87, 51, 0.55)',
  'rgba(199, 0, 57, 0.55)',
  'rgba(144, 12, 63, 0.55)',
  'rgba(88, 24, 69, 0.55)',
  'rgba(218, 247, 166, 0.55)',
  'rgba(255, 195, 51, 0.55)',
];

/**
 * The configured default overlay colour for a study subject.
 *
 * <p>The same lookup this dialog's own colour effect performs, exported so a caller that draws the
 * same intervals without opening the dialog gets the same colour. A second palette elsewhere would
 * mean one study drawn two different ways depending on which control started it.
 */
export function defaultSubjectOverlayColor(subject) {
  const idx = RETRO_PLANETS.indexOf(subject);
  return OVERLAY_COLORS[(idx === -1 ? 0 : idx) % OVERLAY_COLORS.length];
}


/**
 * THE FIELDS A PREFILL MAY CARRY — `dialog.open:areaHighlight`.
 *
 * <p>⚠ ONE FLAT LIST FOR SEVEN STUDIES, and the names are the form's own rather than the wire's.
 * This dialog holds seven independent sections with their own planet pickers, so there is no
 * single `planet` field to set: `planetSignPlanet` and `retroComplementPlanet` are different
 * controls and prefilling the wrong one silently configures a section the reader is not looking
 * at. The Copilot's job is to name the section by naming its fields.
 *
 * <p>⚠ AND THE SIGN IS A NAME HERE, where the headless path sends an INDEX. The form's state is
 * the string the dropdown shows; the endpoint takes a zero-based position. Two different values
 * for one idea, which is exactly why `study.run:areaHighlight` and this prefill do not share a
 * config shape.
 *
 * <p>Kept in step with `CopilotTools.PREFILL_FIELDS` by `CopilotToolsPrefillTest` — a field
 * listed in one and not the other is a prefill the backend offers and the dialog refuses, which
 * means the dialog does not open at all.
 */
export const PREFILL_FIELDS = Object.freeze([
  'color',
  // planet in sign
  'planetSignPlanet', 'planetSignSign', 'planetSignZodiac',
  // planet in nakshatra
  'planetNakshatraPlanet', 'planetNakshatra', 'planetNakshatraZodiac',
  // planet in nakshatra charan
  'planetCharanPlanet', 'planetCharanNakshatra', 'planetCharanCharan', 'planetCharanZodiac',
  // planet in a degree range
  'planetFrom', 'fromDegree', 'toDegree', 'zodiacType1',
  // the three retrograde studies
  'retroPlanet', 'directPlanet', 'retroComplementPlanet',
]);

const AreaHighlightDialog = ({
  open, onClose, barData, extendedBarData, onRun, onJournalAppend, initialConfig,
}) => {
  const { user, api, isAdmin, loading: authLoading } = useAuth();
  const { addOverlay } = useChartOverlays();

  // Recomputed every render, so a profile that arrives after the dialog is already open adds the
  // fourth frame as soon as it lands rather than on the next time it is opened.
  const adminAllowed = isAdminUser(isAdmin, user);
  const [lastRunStudy, setLastRunStudy] = useState(null);
  const [color, setColor] = useState(OVERLAY_COLORS[0]);

  // Planet in Degree Range state
  const [planetFrom, setPlanetFrom] = useState(PLANETS[0]);
  const [fromDegree, setFromDegree] = useState('30');
  const [toDegree, setToDegree] = useState('60');
  const [zodiacType1, setZodiacType1] = useState(ZODIAC_TYPES[0]);

  // Planet in Sign state
  const [planetSignPlanet, setPlanetSignPlanet] = useState(PLANETS[0]);
  const [planetSignSign, setPlanetSignSign] = useState(SIGNS[0]);
  const [planetSignZodiac, setPlanetSignZodiac] = useState(ZODIAC_TYPES[0]);

  // Nakshatra and charan frames. Both studies used to send a hardcoded "sidereal" from the backend
  // controller, on the reasoning that nakshatras are sidereal divisions. The DIVISIONS are; the
  // question "is this body inside that arc" is not, and it is the same sector search the sign
  // study runs. Defaulting to geo-sidereal keeps the familiar answer where it was.
  const [planetNakshatraZodiac, setPlanetNakshatraZodiac] = useState(ZODIAC_TYPES[0]);
  const [planetCharanZodiac, setPlanetCharanZodiac] = useState(ZODIAC_TYPES[0]);

  // Switching to a heliocentric frame can invalidate the current body, and a Select showing a
  // value that is no longer among its options renders blank rather than reporting the problem.
  useEffect(() => {
    if (NO_HELIOCENTRIC_POSITION.includes(planetFrom) && isHeliocentricFrame(zodiacType1)) {
      setPlanetFrom(fallbackPlanet(zodiacType1));
    }
  }, [zodiacType1, planetFrom]);

  useEffect(() => {
    if (NO_HELIOCENTRIC_POSITION.includes(planetSignPlanet) && isHeliocentricFrame(planetSignZodiac)) {
      setPlanetSignPlanet(fallbackPlanet(planetSignZodiac));
    }
  }, [planetSignZodiac, planetSignPlanet]);


  // The ONLY thing that may change a frame the user chose, and only in one case: a non-admin
  // holding a heliocentric frame, which the endpoint would refuse with a 403 they could not act
  // on. It reads ADMIN_ONLY_ZODIAC_TYPES rather than naming a frame, so restricting another one
  // needs no edit here — which is what made the 2026-10-19 change a one-line list edit.
  //
  // Nothing else resets the frame. A study used to put it back to geo-sidereal when it ran, which
  // made a deliberate choice look like it had been ignored — pick Heliocentric Tropical, press Run,
  // watch the box say Geocentric Sidereal — and left the next run on the default unless the user
  // noticed and re-picked. A frame now stays selected until it is changed.
  //
  // Nothing happens while auth is still resolving either. During that window every user looks like
  // a non-admin, and clearing the selection then would undo an admin's choice on a token refresh.
  useEffect(() => {
    if (authLoading || adminAllowed) return;
    if (ADMIN_ONLY_ZODIAC_TYPES.includes(zodiacType1)) setZodiacType1(ZODIAC_TYPES[0]);
    if (ADMIN_ONLY_ZODIAC_TYPES.includes(planetSignZodiac)) setPlanetSignZodiac(ZODIAC_TYPES[0]);
    if (ADMIN_ONLY_ZODIAC_TYPES.includes(planetNakshatraZodiac)) setPlanetNakshatraZodiac(ZODIAC_TYPES[0]);
    if (ADMIN_ONLY_ZODIAC_TYPES.includes(planetCharanZodiac)) setPlanetCharanZodiac(ZODIAC_TYPES[0]);
  }, [authLoading, adminAllowed, zodiacType1, planetSignZodiac, planetNakshatraZodiac, planetCharanZodiac]);

  // Says, in the console, exactly why the frame list is the length it is. An admin missing the
  // fourth option has several possible causes that look identical on screen — a profile fetch that
  // fell back, a subscriptionType that is not ADMIN, auth still loading — and this distinguishes
  // them without a debugger.
  useEffect(() => {
    if (!open) return;
    console.log('[AreaHighlightDialog] frame options', {
      frames: zodiacTypesFor(adminAllowed, null),
      adminAllowed,
      contextIsAdmin: isAdmin,
      authLoading,
      subscriptionType: user?.subscriptionType,
      adminField: user?.admin,
      role: user?.role,
      usingFallbackProfile: Boolean(user?.fallback),
    });
  }, [open, adminAllowed, isAdmin, authLoading, user]);

  // Auto-select color based on dropdown selection
  React.useEffect(() => {
    // For Planet in Sign: use planetSignSign index
    const idx = SIGNS.indexOf(planetSignSign);
    if (idx !== -1) {
      setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
    }
  }, [planetSignSign]);

  // Planet in Nakshatra state
  const [planetNakshatraPlanet, setPlanetNakshatraPlanet] = useState(PLANETS[0]);
  const [planetNakshatra, setPlanetNakshatra] = useState(NAKSHATRAS[0]);

  // For Planet in Nakshatra: use planetNakshatra index
  React.useEffect(() => {
    const idx = NAKSHATRAS.indexOf(planetNakshatra);
    if (idx !== -1) {
      setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
    }
  }, [planetNakshatra]);

  // Planet in Nakshatra Charan state
  const [planetCharanPlanet, setPlanetCharanPlanet] = useState(PLANETS[0]);
  const [planetCharanNakshatra, setPlanetCharanNakshatra] = useState(NAKSHATRAS[0]);
  const [planetCharanCharan, setPlanetCharanCharan] = useState(CHARANS[0]);

  // Declared AFTER the two bodies they read, not next to the sign study's equivalent effect.
  // A hook that names a `const` declared further down compiles, lints and then throws
  // "Cannot access before initialization" on mount — the whole dialog, not just this row. That is
  // the failure App.tdz.test.js exists for, and it is easy to reintroduce by grouping code by
  // topic rather than by declaration order.
  useEffect(() => {
    if (NO_HELIOCENTRIC_POSITION.includes(planetNakshatraPlanet) && isHeliocentricFrame(planetNakshatraZodiac)) {
      setPlanetNakshatraPlanet(fallbackPlanet(planetNakshatraZodiac));
    }
  }, [planetNakshatraZodiac, planetNakshatraPlanet]);

  useEffect(() => {
    if (NO_HELIOCENTRIC_POSITION.includes(planetCharanPlanet) && isHeliocentricFrame(planetCharanZodiac)) {
      setPlanetCharanPlanet(fallbackPlanet(planetCharanZodiac));
    }
  }, [planetCharanZodiac, planetCharanPlanet]);

  // For Planet in Nakshatra Charan: use planetCharanNakshatra index
  React.useEffect(() => {
    const idx = NAKSHATRAS.indexOf(planetCharanNakshatra);
    if (idx !== -1) {
      setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
    }
  }, [planetCharanNakshatra]);

  // Retro/Direct state
  const [retroPlanet, setRetroPlanet] = useState(RETRO_PLANETS[0]);
  const [directPlanet, setDirectPlanet] = useState(RETRO_PLANETS[0]);
  // Mercury by name, not RETRO_PLANETS[0]. An index-derived default silently becomes a different
  // planet the day the list is reordered, and this one was asked for specifically.
  const [retroComplementPlanet, setRetroComplementPlanet] = useState('Mercury');

  /**
   * INSPECTION MODE — `dialog.open:areaHighlight` arrives here.
   *
   * <p>⚠ PLACED BELOW EVERY SETTER IT CALLS, deliberately. A closure body runs after render, so
   * either order resolves at runtime — but a hook declared above the `const` it names is how this
   * codebase has taken four temporal-dead-zone crashes, and the readable order is the one that
   * cannot be wrong. This block must stay under the last `useState` above it.
   *
   * <p>Only the fields present are applied. A prefill naming one section leaves the other six
   * exactly as the reader left them, which is what makes "let me adjust the Mercury retrograde
   * shading" land on that section without resetting the sign pickers beside it.
   */
  useInitialConfig(initialConfig, (cfg) => {
    if (cfg.color !== undefined) setColor(cfg.color);

    if (cfg.planetSignPlanet !== undefined) setPlanetSignPlanet(cfg.planetSignPlanet);
    if (cfg.planetSignSign !== undefined) setPlanetSignSign(cfg.planetSignSign);
    if (cfg.planetSignZodiac !== undefined) setPlanetSignZodiac(cfg.planetSignZodiac);

    if (cfg.planetNakshatraPlanet !== undefined) setPlanetNakshatraPlanet(cfg.planetNakshatraPlanet);
    if (cfg.planetNakshatra !== undefined) setPlanetNakshatra(cfg.planetNakshatra);
    if (cfg.planetNakshatraZodiac !== undefined) setPlanetNakshatraZodiac(cfg.planetNakshatraZodiac);

    if (cfg.planetCharanPlanet !== undefined) setPlanetCharanPlanet(cfg.planetCharanPlanet);
    if (cfg.planetCharanNakshatra !== undefined) setPlanetCharanNakshatra(cfg.planetCharanNakshatra);
    // Number, because the control's options are the numbers 1-4: a string "3" matches no option
    // and renders the picker empty, which reads as a prefill that did not arrive.
    if (cfg.planetCharanCharan !== undefined) setPlanetCharanCharan(Number(cfg.planetCharanCharan));
    if (cfg.planetCharanZodiac !== undefined) setPlanetCharanZodiac(cfg.planetCharanZodiac);

    if (cfg.planetFrom !== undefined) setPlanetFrom(cfg.planetFrom);
    // Strings, because these two are text inputs: a number makes the input switch between
    // controlled and uncontrolled as the value changes, and React warns and loses the cursor.
    if (cfg.fromDegree !== undefined) setFromDegree(String(cfg.fromDegree));
    if (cfg.toDegree !== undefined) setToDegree(String(cfg.toDegree));
    if (cfg.zodiacType1 !== undefined) setZodiacType1(cfg.zodiacType1);

    if (cfg.retroPlanet !== undefined) setRetroPlanet(cfg.retroPlanet);
    if (cfg.directPlanet !== undefined) setDirectPlanet(cfg.directPlanet);
    if (cfg.retroComplementPlanet !== undefined) {
      setRetroComplementPlanet(cfg.retroComplementPlanet);
    }
  });

  // For Retrograde to Direct: use retroPlanet index
  React.useEffect(() => {
    const idx = RETRO_PLANETS.indexOf(retroPlanet);
    if (idx !== -1) {
      setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
    }
  }, [retroPlanet]);

  // For Direct to Retrograde: use directPlanet index
  React.useEffect(() => {
    const idx = RETRO_PLANETS.indexOf(directPlanet);
    if (idx !== -1) {
      setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
    }
  }, [directPlanet]);

  // For Retrograde Complement: same palette rule as its two neighbours, so one planet keeps one
  // hue across all three studies. The two SHADES this study draws are derived from it.
  React.useEffect(() => {
    const idx = RETRO_PLANETS.indexOf(retroComplementPlanet);
    if (idx !== -1) {
      setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
    }
  }, [retroComplementPlanet]);


  const handleApiCall = async (url, payload, studyLabel, processOverlays) => {
    // Log the service call before making the request
    console.log(`[AreaHighlightDialog] Calling service:`, { url, payload, studyLabel });
    // The frame as it goes on the wire, on its own line. The object above is easy to skim past,
    // and this is the one value worth being able to read at a glance against what the dropdown
    // shows and against what the server logs it parsed.
    console.log('[AreaHighlight API Payload]', { url, zodiac: payload.zodiac, planet: payload.planet, sign: payload.sign });
    try {
      const response = await api.post(url, payload);
      const data = response.data;
      console.log('[DEBUG] Full backend response:', JSON.stringify(data, null, 2));
      console.log('[AreaHighlightDialog] Backend response:', data);
      // Expect: {datePairs: [...], summaryLines: [...], futureDates: [...]}
      console.log('[Diagnostics] Received full data:', data);
      if (data) {
        processOverlays(data, studyLabel);
      } else {
        console.log(`Study '${studyLabel}' returned no data.`);
      }
    } catch (error) {
      console.error(`Failed to run study '${studyLabel}':`, error);
    }
  };

  /**
   * The frame a run is using, pinned for the whole run.
   *
   * Everything downstream — the payload, the log line, and the overlay's params — reads this
   * local rather than the state variable. The overlay is built in an async callback that lands
   * after the request returns, so reading state there reports whatever the frame happens to be by
   * then, not the frame the study was actually computed in. That is how an overlay came to be
   * labelled geo-sidereal for a run that went out as helio.
   */
  const handleDegreeRangeRun = async () => {
    const frame = zodiacType1;
    console.log('[AreaHighlight RUN] Selected frame before dispatch:', frame);
    setLastRunStudy('degreeRange');
    await logActivity(api, user, 'RUN_STUDY', {
      studyType: 'planetInDegreeRange',
      parameters: { planet: planetFrom, fromDegree, toDegree, zodiac: frame }
    });
    console.log('[AreaHighlightDialog] handleDegreeRangeRun triggered', { planetFrom, fromDegree, toDegree, frame, barData });
    // Tagged with the frame it ran in, so two bands from different frames are told apart
    // on the chart. `frame` is pinned above, so this cannot drift from what was sent.
    const studyLabel = `${withFrame(planetFrom, frame)} ${fromDegree}-${toDegree}`;
    const payload = {
      planet: planetFrom,
      fromDegree: fromDegree,
      toDegree: toDegree,
      zodiac: frame,
      startDateTime: barData.dates[0],
      endDateTime: barData.dates[barData.dates.length - 1],
      symbol: barData.symbol || undefined,
    };
    const url = '/api/area-highlight-study/runAreaHighlightStudy';
    handleApiCall(url, payload, studyLabel, (data) => {
      addAreaHighlightOverlaysAndMessages({
        isAdmin,
        datePairs: data.datePairs || [],
        summaryLines: data.summaryLines || [],
        futureDates: data.futureDates || [],
        params: {
          color,
          label: studyLabel,
          planet1: planetFrom,
          zodiac: frame,
          fromDegree,
          toDegree
        },
        addOverlay,
        barData,
        extendedBarData,
        updateMessagesPanel: typeof onRun === 'function' ? () => {
          onRun(data);
        } : undefined,
        onJournalAppend,
      });
    });
  };

  const handlePlanetInSignRun = async () => {
    const frame = planetSignZodiac;
    console.log('[AreaHighlight RUN] Selected frame before dispatch:', frame);
    setLastRunStudy('planetInSign');
    await logActivity(api, user, 'RUN_STUDY', {
      studyType: 'planetInSign',
      parameters: { planet: planetSignPlanet, sign: planetSignSign, zodiac: frame }
    });
    console.log('[AreaHighlightDialog] handlePlanetInSignRun triggered', { planetSignPlanet, planetSignSign, frame, barData, extendedBarData });
    const payload = {
      planet: planetSignPlanet,
      sign: SIGNS.indexOf(planetSignSign),
      zodiac: frame,
      startDateTime: barData.dates[0],
      endDateTime: barData.dates[barData.dates.length - 1],
      symbol: barData.symbol || undefined,
    };
    const dynamicLabel = `${withFrame(planetSignPlanet, frame)} in ${planetSignSign}`;
    handleApiCall(
      '/api/area-highlight-study/planet-in-sign',
      payload,
      dynamicLabel,
      (data) => {
        const { datePairs, summaryLines, futureDates } = data;
        console.log('[AreaHighlightDialog] [Planet in Sign] Passing to overlays/messages:', { datePairs, summaryLines, futureDates });
        addAreaHighlightOverlaysAndMessages({
        isAdmin,
          datePairs: datePairs || [],
          summaryLines: summaryLines || [],
          futureDates: futureDates || [],
          params: {
            color,
            label: dynamicLabel,
            planet1: planetSignPlanet,
            sign: planetSignSign,
            zodiac: frame,
          },
          addOverlay,
          barData,
          extendedBarData,
          updateMessagesPanel: typeof onRun === 'function' ? () => {
            onRun(data);
          } : undefined,
          onJournalAppend,
        });
      }
    );
  };

  const handlePlanetInNakshatraRun = async () => {
    // Pinned before the await, like the degree-range and sign runs: the overlay is built in a
    // callback that lands after the request returns, so reading state there labels the band with
    // whatever the frame happens to be by then rather than what it was computed in.
    const frame = planetNakshatraZodiac;
    setLastRunStudy('planetInNakshatra');
    await logActivity(api, user, 'RUN_STUDY', {
      studyType: 'planetInNakshatra',
      parameters: { planet: planetNakshatraPlanet, nakshatra: planetNakshatra, zodiac: frame }
    });
    console.log('[AreaHighlightDialog] handlePlanetInNakshatraRun triggered', { planetNakshatraPlanet, planetNakshatra, frame, barData });
    const payload = {
      planet: planetNakshatraPlanet,
      nakshatra: planetNakshatra,
      zodiac: frame,
      startDateTime: barData.dates[0],
      endDateTime: barData.dates[barData.dates.length - 1],
      symbol: barData.symbol,
    };
    handleApiCall(
      '/api/area-highlight-study/planet-in-nakshatra',
      payload,
      `Planet in Nakshatra: ${withFrame(planetNakshatraPlanet, frame)} in ${planetNakshatra}`,
      (data) => {
        const { datePairs, summaryLines, futureDates } = data;
        console.log('[AreaHighlightDialog] [Nakshatra] Passing to overlays/messages:', { datePairs, summaryLines, futureDates });
        addAreaHighlightOverlaysAndMessages({
        isAdmin,
          datePairs: datePairs || [],
          summaryLines: summaryLines || [],
          futureDates: futureDates || [],
          params: {
            color,
            label: `Planet in Nakshatra: ${planetNakshatraPlanet} in ${planetNakshatra}`,
            planet1: planetNakshatraPlanet,
            nakshatra: planetNakshatra,
            extendedDates: extendedBarData?.dates,
            realBarEndDate: barData.dates[barData.dates.length - 1],
          },
          addOverlay,
          barData,
          extendedBarData,
          updateMessagesPanel: typeof onRun === 'function' ? () => {
            onRun(data);
          } : undefined,
          onJournalAppend,
        });
      }
    );
  };

  const handlePlanetInNakshatraCharanRun = async () => {
    const frame = planetCharanZodiac;
    setLastRunStudy('planetInNakshatraCharan');
    await logActivity(api, user, 'RUN_STUDY', {
      studyType: 'planetInNakshatraCharan',
      parameters: { planet: planetCharanPlanet, nakshatra: planetCharanNakshatra, charan: planetCharanCharan, zodiac: frame }
    });
    console.log('[AreaHighlightDialog] handlePlanetInNakshatraCharanRun triggered', { planetCharanPlanet, planetCharanNakshatra, planetCharanCharan, frame, barData });
    const payload = {
      planet: planetCharanPlanet,
      nakshatra: planetCharanNakshatra,
      charan: planetCharanCharan,
      zodiac: frame,
      startDateTime: barData.dates[0],
      endDateTime: barData.dates[barData.dates.length - 1],
      symbol: barData.symbol,
    };
    const dynamicLabel = `Planet in Nakshatra Charan: ${withFrame(planetCharanPlanet, frame)} in ${planetCharanNakshatra} Charan ${planetCharanCharan}`;
    handleApiCall(
      '/api/area-highlight-study/planet-in-nakshatra-charan',
      payload,
      dynamicLabel,
      (data) => {
        const { datePairs, summaryLines, futureDates } = data;
        console.log('[AreaHighlightDialog] [NakshatraCharan] Passing to overlays/messages:', { datePairs, summaryLines, futureDates });
        addAreaHighlightOverlaysAndMessages({
        isAdmin,
          datePairs: datePairs || [],
          summaryLines: summaryLines || [],
          futureDates: futureDates || [],
          params: {
            color,
            label: dynamicLabel,
            planet1: planetCharanPlanet,
            nakshatra: planetCharanNakshatra,
            charan: planetCharanCharan,
            extendedDates: extendedBarData?.dates,
            realBarEndDate: barData.dates[barData.dates.length - 1],
          },
          addOverlay,
          barData,
          extendedBarData,
          updateMessagesPanel: typeof onRun === 'function' ? () => {
            onRun(data);
          } : undefined,
          onJournalAppend,
        });
      }
    );
  };

  const handleRetroComplementRun = async () => {
    setLastRunStudy('retroComplement');
    const planetToUse = retroComplementPlanet;
    await logActivity(api, user, 'RUN_STUDY', {
      studyType: 'planetRetroComplement',
      parameters: { planet: planetToUse }
    });

    const labels = retroComplementLabels(planetToUse);
    const shades = complementShades(color);
    const payload = {
      planet: planetToUse,
      startDateTime: barData.dates[0],
      endDateTime: barData.dates[barData.dates.length - 1],
      symbol: barData.symbol || undefined,
    };

    handleApiCall(
      '/api/area-highlight-study/planet-retro-complement',
      payload,
      labels.retrace,
      (data) => {
        // The retrograde loop first, in the weaker shade, and WITHOUT the report. It is drawn as
        // context for the band beside it; the study's summary describes the retrace, so attaching
        // the lines here as well would print the same report twice under two different headings.
        addAreaHighlightOverlaysAndMessages({
          isAdmin,
          datePairs: data.retrogradePairs || [],
          summaryLines: [],
          futureDates: [],
          params: {
            color: shades.loop,
            label: labels.loop,
            planet1: planetToUse,
          },
          addOverlay,
          barData,
          extendedBarData,
          onJournalAppend,
        });

        // Then the retrace, in the stronger shade, carrying the report. `retroType` reaches only
        // this half, so the horizontal price bands mark the period the statistics actually cover.
        addAreaHighlightOverlaysAndMessages({
          isAdmin,
          datePairs: data.retracePairs || [],
          summaryLines: data.summaryLines || [],
          futureDates: data.futureDates || [],
          params: {
            color: shades.retrace,
            label: labels.retrace,
            planet1: planetToUse,
            retroType: 'retro-complement',
          },
          addOverlay,
          barData,
          extendedBarData,
          updateMessagesPanel: typeof onRun === 'function' ? () => {
            onRun(data);
          } : undefined,
          onJournalAppend,
        });
      }
    );
  };

  const handleRetroDirectRun = async (isRetrograde) => {
    setLastRunStudy(isRetrograde ? 'retroToDirect' : 'directToRetro');
    const planetToUse = isRetrograde ? retroPlanet : directPlanet;
    await logActivity(api, user, 'RUN_STUDY', {
      studyType: isRetrograde ? 'retrogradeToDirect' : 'directToRetrograde',
      parameters: { planet: planetToUse }
    });
    console.log('[AreaHighlightDialog] handleRetroDirectRun triggered', { retroPlanet, directPlanet, barData, isRetrograde });
  // Abbreviated because this string is drawn onto the chart as the area-highlight label, where
  // "Venus Retrograde to Direct" is wide enough to collide with the next study's label. The
  // dialog's own field headings stay spelled out — a form label has room and needs to be plain.
  const studyLabel = retrogradeStudyLabel(planetToUse, isRetrograde);
  const payload = {
    planet: planetToUse,
    startDateTime: barData.dates[0],
    endDateTime: barData.dates[barData.dates.length - 1],
    symbol: barData.symbol || undefined,
  };
  handleApiCall(
    isRetrograde ? '/api/area-highlight-study/planet-retro-direct' : '/api/area-highlight-study/planet-direct-retro',
    payload,
    studyLabel,
    (data) => {
      addAreaHighlightOverlaysAndMessages({
        isAdmin,
        datePairs: data.datePairs || [],
        summaryLines: data.summaryLines || [],
        futureDates: data.futureDates || [],
        params: {
          color,
          label: studyLabel,
          planet1: planetToUse,
          retroType: isRetrograde ? 'retro-direct' : 'direct-retro',
        },
        addOverlay,
        barData,
        extendedBarData,
        updateMessagesPanel: typeof onRun === 'function' ? () => {
          onRun(data);
        } : undefined,
        onJournalAppend,
      });
    }
  );
};


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" sx={{ height: '80vh' }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Area Highlight Studies</span>
        <HelpIcon helpKey="areaHighlightDialog" />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {/* Color Picker */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography sx={{ fontWeight: 'bold' }}>Select Overlay Color:</Typography>
            <Select
              value={color}
              onChange={(e) => setColor(e.target.value)}
              size="small"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, backgroundColor: selected, border: '1px solid #ccc', borderRadius: '4px' }} />
                  {selected}
                </Box>
              )}
              inputProps={{ id: 'area-highlight-color-select' }}
            >
              {OVERLAY_COLORS.map(c => (
                <MenuItem key={c} value={c}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, backgroundColor: c, border: '1px solid #ccc', borderRadius: '4px' }} />
                    {c}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Planet in Degree Range */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'degreeRange' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Planet in Degree Range:</Typography>
            <Select value={planetFrom} onChange={(e) => setPlanetFrom(e.target.value)} size="small">{planetsForFrame(zodiacType1).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <TextField label="From" value={fromDegree} onChange={(e) => setFromDegree(e.target.value)} size="small" sx={{ width: 80 }} />
            <TextField label="To" value={toDegree} onChange={(e) => setToDegree(e.target.value)} size="small" sx={{ width: 80 }} />
            <Select value={zodiacType1} onChange={(e) => setZodiacType1(e.target.value)} size="small" inputProps={{ 'aria-label': 'Frame for Planet in Degree Range' }}>{zodiacTypesFor(adminAllowed, zodiacType1).map(z => <MenuItem key={z} value={z}>{zodiacLabel(z)}</MenuItem>)}</Select>
            <Button onClick={handleDegreeRangeRun} variant="contained" size="small" sx={{ ml: 'auto' }}>Run</Button>
          </Box>

          {/* Planet in Sign */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'planetInSign' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Planet in Sign:</Typography>
            <Select value={planetSignPlanet} onChange={(e) => setPlanetSignPlanet(e.target.value)} size="small">{planetsForFrame(planetSignZodiac).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <Select value={planetSignSign} onChange={(e) => {
  const idx = SIGNS.indexOf(e.target.value);
  setPlanetSignSign(e.target.value);
  setColor(OVERLAY_COLORS[idx % OVERLAY_COLORS.length]);
}} size="small">{SIGNS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select>
            <Select value={planetSignZodiac} onChange={(e) => setPlanetSignZodiac(e.target.value)} size="small" inputProps={{ 'aria-label': 'Frame for Planet in Sign' }}>{zodiacTypesFor(adminAllowed, planetSignZodiac).map(z => <MenuItem key={z} value={z}>{zodiacLabel(z)}</MenuItem>)}</Select>
            <Button onClick={handlePlanetInSignRun} variant="contained" size="small" sx={{ ml: 'auto' }}>Run</Button>
          </Box>

          {/* Planet in Nakshatra */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'planetInNakshatra' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Planet in Nakshatra:</Typography>
            <Select value={planetNakshatraPlanet} onChange={(e) => setPlanetNakshatraPlanet(e.target.value)} size="small">{planetsForFrame(planetNakshatraZodiac).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <Select value={planetNakshatra} onChange={(e) => setPlanetNakshatra(e.target.value)} size="small">{NAKSHATRAS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}</Select>
            <Select value={planetNakshatraZodiac} onChange={(e) => setPlanetNakshatraZodiac(e.target.value)} size="small" inputProps={{ 'aria-label': 'Frame for Planet in Nakshatra' }}>{zodiacTypesFor(adminAllowed, planetNakshatraZodiac).map(z => <MenuItem key={z} value={z}>{zodiacLabel(z)}</MenuItem>)}</Select>
            <Button onClick={handlePlanetInNakshatraRun} variant="contained" size="small" sx={{ ml: 'auto' }}>Run</Button>
          </Box>

          {/* Planet in Nakshatra Charan */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'planetInNakshatraCharan' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Planet in Nakshatra Charan:</Typography>
            <Select value={planetCharanPlanet} onChange={(e) => setPlanetCharanPlanet(e.target.value)} size="small">{planetsForFrame(planetCharanZodiac).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <Select value={planetCharanNakshatra} onChange={(e) => setPlanetCharanNakshatra(e.target.value)} size="small">{NAKSHATRAS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}</Select>
            <Select value={planetCharanCharan} onChange={(e) => setPlanetCharanCharan(e.target.value)} size="small">{CHARANS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}</Select>
            <Select value={planetCharanZodiac} onChange={(e) => setPlanetCharanZodiac(e.target.value)} size="small" inputProps={{ 'aria-label': 'Frame for Planet in Nakshatra Charan' }}>{zodiacTypesFor(adminAllowed, planetCharanZodiac).map(z => <MenuItem key={z} value={z}>{zodiacLabel(z)}</MenuItem>)}</Select>
            <Button
              onClick={handlePlanetInNakshatraCharanRun}
              variant="contained"
              size="small"
              sx={{ ml: 'auto' }}
            >
              Run
            </Button>
          </Box>

          {/* Retrograde to Direct */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'retroToDirect' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Retrograde to Direct:</Typography>
            <Select value={retroPlanet} onChange={(e) => setRetroPlanet(e.target.value)} size="small">{RETRO_PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <Button onClick={() => handleRetroDirectRun(true)} variant="contained" size="small" sx={{ ml: 'auto' }}>Run</Button>
          </Box>

          {/* Retrograde Complement — the loop, and the direct advance that undoes it */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'retroComplement' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Retrograde Complement:</Typography>
            <Select value={retroComplementPlanet} onChange={(e) => setRetroComplementPlanet(e.target.value)} size="small">{RETRO_PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <Button onClick={handleRetroComplementRun} variant="contained" size="small" sx={{ ml: 'auto' }}>Run</Button>
          </Box>

          {/* Direct to Retrograde */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: lastRunStudy === 'directToRetro' ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 1, transition: 'border 0.3s' }}>
            <Typography sx={{ minWidth: 200, fontWeight: 'bold' }}>Direct to Retrograde:</Typography>
            <Select value={directPlanet} onChange={(e) => setDirectPlanet(e.target.value)} size="small">{RETRO_PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select>
            <Button onClick={() => handleRetroDirectRun(false)} variant="contained" size="small" sx={{ ml: 'auto' }}>Run</Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="error">Close</Button>
      </DialogActions>
    </Dialog>
  );
};
AreaHighlightDialog.propTypes = {
  /** Prefill from `dialog.open:areaHighlight`. See PREFILL_FIELDS. */
  initialConfig: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  barData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string).isRequired,
    ohlc: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
    symbol: PropTypes.string,
  }).isRequired,
  extendedBarData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string),
    ohlc: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  }),
  onRun: PropTypes.func,
  onJournalAppend: PropTypes.func,
};

export default AreaHighlightDialog;